<template>
  <div
    :class="{
      'account-open': accountMenuVisible,
    }"
  >
    <div id="page-wrapper" :class="{ desktop: isDesktop }">
      <slot name="header">
        <component :is="isDesktop ? AsyncAppHeaderDesktop : AsyncAppHeader" />
      </slot>

      <slot name="search-wizard" />

      <slot class="nuxt-content" />

      <slot name="footer">
        <template v-if="isDesktop">
          <AsyncRecentCampsites
            :limit="12"
            :key="getAsyncComponentKey('recentCampsitesDesktop')"
            @vue:mounted="setAsyncComponentKey('recentCampsitesDesktop')"
          />
          <AsyncAppFooterDesktop
            :key="getAsyncComponentKey('appFooterDesktop')"
            :total-counts="totalCounts"
            @vue:mounted="setAsyncComponentKey('appFooterDesktop')"
          />
        </template>
        <template v-else>
          <AsyncRecentCampsites
            :limit="6"
            :key="getAsyncComponentKey('recentCampsitesMobile')"
            @vue:mounted="setAsyncComponentKey('recentCampsitesMobile')"
          />
          <AsyncAppFooter
            :key="getAsyncComponentKey('appFooter')"
            @vue:mounted="setAsyncComponentKey('appFooter')"
          />
        </template>
      </slot>
    </div>

    <AsyncAccountHome
      v-if="accountMenuVisible && !isErrorPage"
      :key="getAsyncComponentKey('accountHome')"
      @vue:mounted="setAsyncComponentKey('accountHome')"
    />
    <AsyncAppMenu
      v-if="!isDesktop && !isErrorPage"
      v-show="appMenuStore.appMenuVisible"
      :app-menu-is-visible="appMenuStore.appMenuVisible"
      :total-counts="totalCounts"
    />

    <!-- Add Google One Tap in a non-intrusive position -->
    <client-only v-if="!isErrorPage">
      <div class="one-tap-container">
        <AsyncGoogleOneTap />
      </div>
    </client-only>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from 'nuxt/app'
import {
  computed,
  defineAsyncComponent,
  onBeforeUpdate,
  onBeforeUnmount,
  ref,
  hydrateOnIdle,
} from 'vue'
import { hydrateOnVisibleSafe } from '../utility/hydration/hydrateOnVisibleSafe'

// Track if this instance is unmounting to prevent updates during transitions
let isUnmounting = false

onBeforeUpdate(() => {
  // Prevent updates if we're unmounting to avoid DOM conflicts during navigation
  if (isUnmounting) {
    return false
  }
  return true
})

onBeforeUnmount(() => {
  isUnmounting = true
})

// Initialize stores
const pinia = usePinia()
const appMenuStore = useAppMenuStore(pinia)
const searchStore = useSearchStoreSetup(pinia)
const desktopStore = useDesktopStore(pinia)
const route = useRoute()

// Async component key management to prevent unnecessary remounts
const asyncComponentKeys = ref<Record<string, string>>({})

const getAsyncComponentKey = (componentName: string) => {
  return (
    asyncComponentKeys.value[componentName] ||
    `${route.fullPath.split('#')[0]}-${componentName}`
  )
}

const setAsyncComponentKey = (componentName: string) => {
  asyncComponentKeys.value[componentName] =
    `${route.fullPath.split('#')[0]}-${componentName}`
}

// Lazy components with hydration strategies
const AsyncAppHeaderDesktop = defineAsyncComponent({
  loader: () => import('../components/layouts/app-header-desktop.vue'),
  hydrate: hydrateOnIdle(10000),
})

const AsyncAppHeader = defineAsyncComponent({
  loader: () => import('../components/layouts/app-header.vue'),
  hydrate: hydrateOnIdle(10000),
})

const AsyncRecentCampsites = defineAsyncComponent({
  loader: () => import('../components/layouts/components/RecentCampsites.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppFooterDesktop = defineAsyncComponent({
  loader: () => import('../components/layouts/AppFooterDesktop.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppFooter = defineAsyncComponent({
  loader: () => import('../components/layouts/AppFooter.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAccountHome = defineAsyncComponent({
  loader: () => import('../components/layouts/AccountHome.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppMenu = defineAsyncComponent({
  loader: () => import('../components/layouts/components/app-menu/AppMenu.vue'),
  hydrate: hydrateOnIdle(10000),
})

const AsyncGoogleOneTap = defineAsyncComponent({
  loader: () => import('../components/login/GoogleOneTap.vue'),
  hydrate: hydrateOnIdle(10000), // Hydrate after 10s idle
})

// Computed properties
const isDesktop = computed(() => desktopStore.isDesktop)

const totalCounts = computed(
  () =>
    searchStore.searchCounts?.bookableCount || searchStore.searchCounts?.count,
)

const accountMenuVisible = computed(() => appMenuStore?.accountMenuVisible)

// Don't render portals during error pages to avoid cascading failures
const isErrorPage = computed(() => {
  const nuxtApp = useNuxtApp()
  return !!nuxtApp.payload.error
})

// Provide isErrorPage to all child components so they can skip API calls during errors
provide('isErrorPage', isErrorPage)
</script>

<style scoped>
#page-wrapper {
  align-items: center;
  background-color: white;
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  min-height: 100%;
  height: 100%;
  position: relative;
  transition: transform 0.35s cubic-bezier(0.23, 1, 0.32, 1);
  width: 100vw;
  z-index: 3;
}

.nuxt-content {
  min-height: 100%;
  height: 100%;
}

.account-open :deep(#user) {
  z-index: 2;
}

.account-open.pagemask {
  pointer-events: none;
}

.account-open :deep(#page-wrapper),
[data-menu-open='true'] #page-wrapper {
  position: fixed;
  transform: translateX(0);
  box-shadow: 0 0 4px 2px rgba(0, 0, 0, 0.35);
}

[data-menu-open='true'] #page-wrapper {
  transform: translateX(85vw);
}

.account-open :deep(#page-wrapper) {
  transform: translateX(-85vw);
}

.account-open :deep(#page-wrapper),
[data-menu-open='true'] #page-wrapper {
  overflow: hidden;
}

.account-open .nuxt-content,
[data-menu-open='true'] .nuxt-content {
  z-index: -2;
  position: relative;
  overflow-x: visible;
  min-height: 100vh;
}

.account-open :deep(.search-summary-bar),
[data-menu-open='true'] .search-summary-bar {
  z-index: 1;
}

.account-open :deep(.nav-bar .account-icon),
.account-open :deep(#back-icon) {
  display: block;
  background: white;
  margin: 0;
}

.account-open :deep(#account-icon) {
  display: none;
}

.account-open :deep(.nav-label.account) {
  display: none;
}

/* desktop & tablet */
#page-wrapper.desktop {
  width: 100%;
}

[data-menu-open='true'] #page-wrapper.desktop {
  transform: translateX(0);
}

.account-open :deep(#page-wrapper.desktop) {
  transform: translateX(-50vw);
}

.one-tap-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000; /* High enough to be above most content but below modals */
  pointer-events: none; /* Allow clicks to pass through the container */
}
.one-tap-container :deep(#one-tap-container) {
  pointer-events: auto; /* Re-enable clicks just for the Google prompt */
}
</style>
